<script setup>
import { defineProps, defineEmits, ref, watch } from 'vue';
import { useLayoutComponents } from '@/composables/layout';
import TextInput from './forms/inputs/text-input.vue';
import InputError from './forms/inputs/input-error.vue';
import ButtonLoading from './button-loading.vue';

const { PrimaryButton } = useLayoutComponents();

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: 'Confirmation'
    },
    message: {
        type: String,
        required: true
    },
    confirmText: {
        type: String,
        default: 'Confirm'
    },
    cancelText: {
        type: String,
        default: 'Cancel'
    },
    type: {
        type: String,
        default: 'info',
        validator: (value) => ['info', 'warning', 'danger', 'success'].includes(value)
    },
    showInput: {
        type: Boolean,
        default: false
    },
    inputLabel: {
        type: String,
        default: ''
    },
    inputPlaceholder: {
        type: String,
        default: ''
    },
    inputRequired: {
        type: Boolean,
        default: false
    },
    defaultValue: {
        type: String,
        default: ''
    },
    showCloseBtn: {
        type: Boolean,
        default: true
    },
    showPasswordInput: {
        type: Boolean,
        default: false
    },
    passwordLabel: {
        type: String,
        default: 'Password'
    },
    passwordPlaceholder: {
        type: String,
        default: 'Enter your password'
    },
    passwordRequired: {
        type: Boolean,
        default: false
    },
    errorMessage: {
        type: String,
        default: ''
    },
    isLoading: {
        type: Boolean,
        default: false
    },
    loadingText: {
        type: String,
        default: 'Loading...'
    }
});

const emit = defineEmits(['close', 'confirm']);
const inputValue = ref(props.defaultValue);
const passwordValue = ref('');
const alertRef = ref(null);

const cancelBtnRef = ref(null);
const inputRef = ref(null);
const passwordRef = ref(null);
const focusableElements = ref([]);

watch(() => props.show, (newVal) => {
    if (newVal) {
        passwordError.value = '';
        updateFocusableElements();
        if (props.showInput && inputRef.value) {
            inputRef.value.focus();
        } else if (props.showPasswordInput && passwordRef.value) {
            passwordRef.value.focus();
        } else if (cancelBtnRef.value) {
            cancelBtnRef.value.focus();
        } else if (focusableElements.value.length > 0) {
            focusableElements.value[0].focus();
        }
    }
});

watch(() => props.defaultValue, (newValue) => {
    inputValue.value = newValue;
}, { immediate: true });

const isLoading = ref(false);
const passwordError = ref('');

watch(() => props.isLoading, (newVal) => {
    isLoading.value = newVal;
});

const handleConfirm = async () => {
   
    if (props.passwordRequired && !passwordValue.value) {
        passwordError.value = 'Password is required to confirm this action.';
        return;
    }
    
    passwordError.value = '';
    isLoading.value = true;
    try {
        await emit('confirm', { 
            input: inputValue.value, 
            password: passwordValue.value 
        });
    } finally {
        isLoading.value = false;
    }
};

const handleClose = () => {
    passwordError.value = '';
    emit('close');
};

const updateFocusableElements = () => {
    if (!alertRef.value) return;
    focusableElements.value = Array.from(
        alertRef.value.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
    );
};

const getAlertIcon = () => {
    switch (props.type) {
        case 'warning':
            return `<svg class="w-6 h-6 text-yellow-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>`;
        case 'danger':
            return `<svg class="w-6 h-6 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>`;
        case 'success':
            return `<svg class="w-6 h-6 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>`;
        default: // info
            return `<svg class="w-6 h-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>`;
    }
};
</script>

<template>
    <Teleport to="body">
        <div v-if="show" class="alert-overlay" data-modal>
            <div ref="alertRef" class="alert" :class="`alert--${type}`" aria-modal="true" :aria-labelledby="'alert-title-' + _uid" :aria-describedby="'alert-message-' + _uid">
                <div class="alert__header">
                    <div class="alert__icon-wrapper" :class="`alert__icon-wrapper--${type}`">
                        <div class="alert__icon" v-html="getAlertIcon()"></div>
                    </div>
                    <h3 class="alert__title" :id="'alert-title-' + _uid">{{ title }}</h3>
                    <button v-if="showCloseBtn" class="alert__close-btn" @click="handleClose" aria-label="Close">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                <div class="alert__content">
                    <p class="alert__message" :id="'alert-message-' + _uid">{{ message }}</p>
                    
                    <div v-if="showInput" class="alert__input">
                        <label v-if="inputLabel" class="alert__input-label" :for="'alert-input-' + _uid">{{ inputLabel }}</label>
                        <TextInput
                            :id="'alert-input-' + _uid"
                            ref="inputRef"
                            v-model="inputValue"
                            :placeholder="inputPlaceholder"
                            :required="inputRequired"
                            class="alert__text-input"
                            @keyup.enter="handleConfirm"
                            @keyup.esc="handleClose"
                        />
                    </div>
                    
                    <div v-if="showPasswordInput" class="alert__input mt-4">
                        <div class="alert__input-header">
                            <label v-if="passwordLabel" class="alert__input-label" :for="'alert-password-' + _uid">{{ passwordLabel }}</label>
                            <InputError class="ml-2" :message="passwordError || errorMessage" />
                        </div>
                        <TextInput
                            :id="'alert-password-' + _uid"
                            ref="passwordRef"
                            v-model="passwordValue"
                            type="password"
                            :placeholder="passwordPlaceholder"
                            :required="passwordRequired"
                            class="alert__text-input"
                            @keyup.enter="handleConfirm"
                            @keyup.esc="handleClose"
                        />
                    </div>
                    
                    <div class="alert__custom mt-4">
                        <slot name="custom"></slot>
                    </div>
                </div>
                
                <div class="alert__footer">
                    <button 
                        ref="cancelBtnRef" 
                        class="btn btn--secondary underline" 
                        @click="handleClose" 
                        :disabled="props.isLoading"
                        :class="{ 'disabled': props.isLoading }"
                    >
                        {{ cancelText }}
                    </button>
                    <PrimaryButton 
                        @click="handleConfirm" 
                        :class="['btn--' + type, { 'disabled cursor-not-allowed': props.isLoading }]"
                        :disabled="props.isLoading || 
                            (inputRequired && !inputValue) ||
                            (passwordRequired && !passwordValue)"
                    >
                        <ButtonLoading 
                            v-if="props.isLoading" 
                            :text="props.loadingText"
                            size="xs" 
                        />
                        <template v-else>{{ confirmText }}</template>
                    </PrimaryButton>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped lang="postcss">
.alert-overlay {
    @apply fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4;
    backdrop-filter: blur(4px);
    animation: fadeIn 0.2s ease;
    isolation: isolate;
}

.alert {
    @apply bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-xl mt-16 flex flex-col overflow-hidden;
    animation: slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    z-index: 1;
}

.alert__header {
    @apply sticky top-0 z-10 flex items-center gap-3 p-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800;
}

.alert__icon-wrapper {
    @apply flex-shrink-0 rounded-lg p-2;
}

.alert__icon-wrapper .alert__icon {
    @apply w-6 h-6;
}

.alert__title {
    @apply font-semibold text-base 2xl:text-lg text-gray-800 dark:text-white;
}

.alert__close-btn {
    @apply ml-auto text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-2 rounded-full transition-colors;
}

.alert__content {
    @apply px-8 py-4 overflow-y-auto;
    flex: 1;
}

.alert__message {
    @apply text-sm 2xl:text-base text-gray-600 dark:text-gray-300 leading-relaxed;
}

.alert__input {
    @apply mt-4;
}

.alert__input-header {
    @apply flex items-center mb-2 w-full;
}

.alert__input-label {
    @apply text-sm font-semibold text-gray-700 dark:text-gray-300;
}

.alert__text-input {
    @apply w-full focus:ring-2 focus:ring-offset-0;
}

.alert__footer {
    @apply sticky bottom-0 z-10 p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-1 bg-white dark:bg-gray-800;
}

.btn {
    @apply px-4 py-2 rounded-lg font-medium transition-colors;
}

.btn--secondary {
    @apply px-4 py-1.5 rounded-lg text-xs 2xl:text-sm transition-colors 
    dark:hover:border-violet-500/50 dark:hover:text-violet-400 
    dark:bg-transparent dark:text-gray-400 dark:border-transparent
    hover:border-gray-300 hover:text-gray-600 
    bg-transparent text-gray-500 border-none;
}

.disabled {
    @apply opacity-50 cursor-not-allowed;
}

.btn--info {
    @apply bg-blue-500 text-white hover:bg-blue-600;
}

.btn--warning {
    @apply bg-yellow-500 text-white hover:bg-yellow-600;
}

.btn--danger {
    @apply bg-red-500 text-white hover:bg-red-600;
}

.btn--success {
    @apply bg-green-500 text-white hover:bg-green-600;
}

.alert__icon-wrapper--info {
    @apply bg-blue-50 dark:bg-blue-900/20;
}

.alert__icon-wrapper--warning {
    @apply bg-yellow-50 dark:bg-yellow-900/20;
}

.alert__icon-wrapper--danger {
    @apply bg-red-50 dark:bg-red-900/20;
}

.alert__icon-wrapper--success {
    @apply bg-green-50 dark:bg-green-900/20;
}

.alert__hint {
    @apply mt-2 text-sm text-gray-500 dark:text-gray-400 italic;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes slideIn {
    from {
        transform: translateY(20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}
</style> 